import { useCallback, useEffect } from 'react';
import {
  ResourceShiftTotalsMap,
  ResourceViolationItem,
  SchedulerDayColumn,
  ServiceRulesViolations,
} from '@lp/features/Scheduler/types';
import { baseRowClassname } from '@lp/features/Scheduler/constants';
import { getIsHighlightedCell, getScheduleDayViolations } from '@lp/features/Scheduler/utils';
import { DayColumnHeader, UserProfileCell, UserProfileHeader } from '@lp/features/Scheduler/components';
import {
  CC_SHIFT_OPTION_DROPDOWN_LIST,
  CC_SHIFT_TYPE_SELECT_LIST,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import { useDayColumns, useSchedulerTableAutoScroll, useSchedulerUserRows } from '@lp/features/Scheduler/hooks';
import { useCCSchedulerFiltersStore, useCCSchedulerHighlightStore } from '@lp/core/store/scheduler';
import {
  CCResource,
  CCResourcesWithShifts,
  SetCCShiftData,
  CCShiftSelectPayload,
  CCShiftType,
} from '@/api/apis/resources/schedule';
import { CCDayCell, CCResourceShiftTotalsTable, CCUserCellSubtitle } from './components';
import { useCCShiftSiblingMaps } from './hooks';

type CCSchedulerTableProps = {
  isEditable: boolean;
  canResetShifts: boolean;
  canUnblockShifts?: boolean;
  selectedDate: Date;
  daysInterval: Date[];
  userList: CCResource[];
  shiftsData: CCResourcesWithShifts;
  onShiftChange: (resourceId: string, day: string, payload?: SetCCShiftData) => void;
  onUnblockShift?: (resourceId: string, day: string, scheduleId: string) => void;
  shiftTotalsMap?: ResourceShiftTotalsMap<CCShiftType>;
  dayViolations?: ServiceRulesViolations;
  resourceViolations?: Record<string, ResourceViolationItem>;
};

export const CCSchedulerTable = ({
  isEditable,
  canResetShifts,
  canUnblockShifts,
  selectedDate,
  daysInterval,
  userList,
  dayViolations,
  resourceViolations,
  shiftsData,
  shiftTotalsMap,
  onShiftChange,
  onUnblockShift,
}: CCSchedulerTableProps) => {
  const filters = useCCSchedulerFiltersStore();
  const highlightValue = useCCSchedulerHighlightStore.use.value();

  const { scrollContainerRef, rowRefCallback, columnRefCallback, scrollToCell } =
    useSchedulerTableAutoScroll(selectedDate);
  const dayCols = useDayColumns(daysInterval, selectedDate);
  const siblingMaps = useCCShiftSiblingMaps(dayCols, userList, shiftsData);
  const { userRows, sortAsc, toggleSortAsc, daysFilters, setDayFilter } = useSchedulerUserRows({
    dayCols,
    userList,
    shiftsData,
    filters,
  });

  useEffect(() => {
    if (highlightValue) {
      const resourceId = highlightValue.resourceId ?? userRows[0]?.id;
      const day = highlightValue.day ?? (dayCols.find((col) => col.isCurrentMonth)?.date as Date);

      scrollToCell(resourceId, day, true);
    }
  }, [dayCols, highlightValue, scrollToCell, userRows]);

  const getShiftChangeHandler = useCallback(
    (resourceId: string, day: string) => (shiftPayload?: CCShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resourceId, day);
        return;
      }

      onShiftChange(resourceId, day, shiftPayload);
    },
    [onShiftChange],
  );

  const getUnblockHandler = useCallback(
    (resourceId: string, day: string) => () => {
      const shift = shiftsData[resourceId]?.days[day];
      if (shift && onUnblockShift) {
        onUnblockShift(resourceId, day, shift.scheduleId);
      }
    },
    [shiftsData, onUnblockShift],
  );

  const renderDayCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const shift = shiftsData[resourceId].days[day];
      const siblingData = siblingMaps[resourceId].get(day);
      const handler = getShiftChangeHandler(resourceId, day);
      const highlight = getIsHighlightedCell(resourceId, column.date, highlightValue);

      return (
        <CCDayCell
          key={column.key}
          column={column}
          shiftData={shift}
          dropdownList={CC_SHIFT_OPTION_DROPDOWN_LIST}
          siblingData={siblingData}
          isEditable={isEditable}
          canReset={canResetShifts}
          canUnblock={canUnblockShifts}
          highlight={highlight}
          onShiftChange={handler}
          onUnblock={canUnblockShifts ? getUnblockHandler(resourceId, day) : undefined}
        />
      );
    },
    [
      isEditable,
      canResetShifts,
      canUnblockShifts,
      shiftsData,
      siblingMaps,
      highlightValue,
      getShiftChangeHandler,
      getUnblockHandler,
    ],
  );

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="w-full overflow-auto">
        <table aria-label="Schedule table" className="relative border-separate border-spacing-0 pb-4 pr-4">
          <thead>
            <tr className="h-10 text-left [&>th]:px-3 [&>th]:text-s-sb first:[&>th]:rounded-s-lg last:[&>th]:rounded-e-lg">
              <UserProfileHeader sortAsc={sortAsc} toggleSortAsc={toggleSortAsc} />
              {dayCols.map((column) => (
                <DayColumnHeader
                  ref={(el) => columnRefCallback(el, column.date)}
                  key={column.key}
                  column={column}
                  withFilter={filters.statusFilters}
                  filterOptionList={CC_SHIFT_TYPE_SELECT_LIST}
                  violations={getScheduleDayViolations(column.key, dayViolations)}
                  selectedKeys={daysFilters[column.key]}
                  onSelectedKeysChange={(statusFilters) => setDayFilter(column.key, statusFilters)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {userRows.map((user) => (
              <tr key={user.id} ref={(el) => rowRefCallback(user.id, el)} className={baseRowClassname}>
                <UserProfileCell key="user" user={user} violation={resourceViolations?.[user.id]}>
                  <CCUserCellSubtitle resourceType={user.type} />
                </UserProfileCell>
                {dayCols.map((column) => renderDayCell(column, user.id))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filters.shiftTotals && <CCResourceShiftTotalsTable userRows={userRows} shiftTotalsMap={shiftTotalsMap} />}
    </div>
  );
};
